import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import Button from './Button';

/**
 * v2 modal + floating panel — radius 14 with the ONE permitted drop shadow
 * (shadow-floating). Everything else in the system stays border-only.
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  /** `sheet` — wide editable tables (the extracted-BOM sheet). */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'sheet';
  /**
   * `false` — the modal must be answered through its own buttons: no close
   * button, Escape and a click outside do nothing (a gate the buyer has to
   * resolve). Default `true`.
   */
  dismissable?: boolean;
  /** Full-bleed body — no padding, clipped corners (tables, media). */
  flush?: boolean;
  /** Focused when the modal opens (e.g. the first input); falls back to the panel. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-[880px]', sheet: 'max-w-[1180px]' };

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, footer, size = 'md', dismissable = true, flush = false, initialFocusRef, children }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !dismissable) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismissable, onClose]);

  // Focus ONLY on the open transition — never re-run when handlers are
  // recreated by parent re-renders, or focus is stolen mid-typing.
  useEffect(() => {
    if (!open) return;
    (initialFocusRef?.current ?? panelRef.current)?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-chrome/40 p-s6 max-md:items-end max-md:p-0"
      onMouseDown={(e) => dismissable && e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`w-full ${widths[size]} rounded-panel border border-line bg-surface shadow-floating focus:outline-none max-md:flex max-md:max-h-[92vh] max-md:max-w-none max-md:flex-col max-md:rounded-b-none ${flush ? 'overflow-hidden' : ''}`}
      >
        <header className="flex items-center justify-between gap-s3 px-s6 py-s5 border-b border-line-divider max-md:shrink-0 max-md:px-s5">
          <h2 className="text-card-header text-ink">{title}</h2>
          {dismissable && <Button variant="ghost" size="sm" aria-label="Close" onClick={onClose} iconLeft={<X />} />}
        </header>
        <div className={`max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto ${flush ? '' : 'px-s6 py-s5 max-md:px-s5'}`}>{children}</div>
        {footer && (
          <footer className="flex justify-end gap-s2 px-s6 py-s4 border-t border-line-divider max-md:shrink-0 max-md:px-s5">{footer}</footer>
        )}
      </div>
    </div>
  );
};

/**
 * Floating panel — same elevation language as the modal, but positioned by
 * the caller (Robbie's floating panel, popovers). `ai` = AI-carrying wash.
 */
export interface FloatingPanelProps {
  ai?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const FloatingPanel: React.FC<FloatingPanelProps> = ({ ai, className = '', children }) => (
  <div
    className={
      `rounded-panel border shadow-floating ` +
      `${ai ? 'border-robbie-border-soft bg-ai-panel' : 'border-line bg-surface'} ` +
      className
    }
  >
    {children}
  </div>
);

export default Modal;
